import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Building2, 
  Mail, 
  Phone, 
  MapPin,
  ExternalLink,
  Calendar,
  Clock,
  Tag,
  CheckCircle,
  CheckCircle2,
  XCircle,
  Loader2,
  Ban,
  AlertTriangle,
  Briefcase,
  Trophy,
  Download,
  Globe,
  RefreshCw,
  UserCheck,
  Image,
  FileText,
  Brain,
  TrendingUp,
  Users,
  HelpCircle,
  Truck,
  Package,
  BarChart3,
  Eye,
  Lightbulb
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import BrandAssets from "@/components/BrandAssets";
import MarkWonModal from "@/components/MarkWonModal";
import ClientIntakeForm from "@/components/ClientIntakeForm";
import CollapsiblePanel from "@/components/CollapsiblePanel";

const ADMIN_BASE = "/admin";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status configs for campaign leads
const campaignStatusConfig = {
  sourced: { label: "Sourced", className: "status-badge bg-muted text-muted-foreground" },
  generating: { label: "Generating", className: "status-info" },
  preview_ready: { label: "Preview Ready", className: "status-info" },
  email_sent: { label: "Email Sent", className: "status-success" },
  call_booked: { label: "Call Booked", className: "status-warning" },
  won: { label: "Won", className: "status-success" },
  lost: { label: "Lost", className: "status-error" }
};

// Status configs for one-off builds
const oneOffStatusConfig = {
  sourced: { label: "Draft", className: "status-badge bg-muted text-muted-foreground" },
  generating: { label: "Generating Preview", className: "status-info" },
  preview_ready: { label: "Preview Ready", className: "status-info" },
  approved: { label: "Approved", className: "status-success" },
  lost: { label: "Cancelled", className: "status-error" }
};

// Helper to get correct status config based on source type
const getStatusConfig = (sourceType) => {
  return sourceType === 'one_off' ? oneOffStatusConfig : campaignStatusConfig;
};

const fullSiteStatusConfig = {
  none: { label: "Not Built", className: "bg-muted text-muted-foreground" },
  building: { label: "Building...", className: "status-warning" },
  ready: { label: "Ready", className: "status-success" },
  failed: { label: "Failed", className: "status-error" }
};

// ============================================================================
// PROSPEO CONTACT HELPER COMPONENTS
// ============================================================================

// Contact Tier Badge (S/A/B/C/D)
const TierBadge = ({ tier, small = false }) => {
  const tierConfig = {
    S: { bg: "bg-emerald-100 text-emerald-800 border-emerald-300", label: "S-Tier" },
    A: { bg: "bg-blue-100 text-blue-800 border-blue-300", label: "A-Tier" },
    B: { bg: "bg-amber-100 text-amber-800 border-amber-300", label: "B-Tier" },
    C: { bg: "bg-gray-100 text-gray-600 border-gray-300", label: "C-Tier" },
    D: { bg: "bg-gray-50 text-gray-500 border-gray-200", label: "D-Tier" }
  };
  
  const config = tierConfig[tier] || tierConfig.C;
  
  return (
    <Badge variant="outline" className={`${config.bg} ${small ? 'text-xs px-1' : ''}`}>
      {small ? tier : config.label}
    </Badge>
  );
};

// Employment Status Badge
const EmploymentStatusBadge = ({ status, small = false }) => {
  const statusConfig = {
    provider_observed: { 
      bg: "bg-blue-50 text-blue-700 border-blue-200", 
      label: "Provider Observed",
      tooltip: "Employment data from Prospeo - not independently verified"
    },
    independently_verified: { 
      bg: "bg-green-50 text-green-700 border-green-200", 
      label: "Verified",
      tooltip: "Employment independently verified"
    },
    uncertain: { 
      bg: "bg-amber-50 text-amber-700 border-amber-200", 
      label: "Uncertain",
      tooltip: "Conflicting employment signals"
    },
    departed: { 
      bg: "bg-red-50 text-red-700 border-red-200", 
      label: "Departed",
      tooltip: "No longer at this company"
    }
  };
  
  const config = statusConfig[status] || statusConfig.provider_observed;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <Badge variant="outline" className={`${config.bg} ${small ? 'text-xs px-1' : ''}`}>
            {small ? (status === 'provider_observed' ? 'Obs' : config.label.substring(0, 3)) : config.label}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">{config.tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Email Status Badge with Enrich Button
const EmailStatusBadge = ({ status, personId, companyId, onEnrich, small = false }) => {
  const [enriching, setEnriching] = useState(false);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  
  const handleEnrich = async () => {
    setEnriching(true);
    try {
      const res = await axios.post(
        `${API}/companies/${companyId}/contacts/${personId}/enrich`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      if (res.data.success) {
        toast.success(`Email found: ${res.data.email}`);
        onEnrich?.();
      } else {
        toast.error(res.data.error || "Email not available");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Enrichment failed");
    } finally {
      setEnriching(false);
    }
  };
  
  if (status === 'enriched') {
    return (
      <Badge variant="outline" className={`bg-green-50 text-green-700 border-green-200 ${small ? 'text-xs' : ''}`}>
        <Mail size={small ? 10 : 12} className="mr-1" /> Enriched
      </Badge>
    );
  }
  
  if (status === 'verified' || status === 'catchall') {
    return (
      <Button 
        variant="outline" 
        size="sm" 
        onClick={handleEnrich}
        disabled={enriching}
        className={`${small ? 'h-6 text-xs px-2' : 'h-7 text-xs'}`}
      >
        {enriching ? (
          <Loader2 size={small ? 10 : 12} className="mr-1 animate-spin" />
        ) : (
          <Mail size={small ? 10 : 12} className="mr-1" />
        )}
        {enriching ? "..." : "Enrich Email"}
      </Button>
    );
  }
  
  if (status === 'unavailable') {
    return (
      <Badge variant="outline" className={`bg-gray-50 text-gray-500 ${small ? 'text-xs' : ''}`}>
        <XCircle size={small ? 10 : 12} className="mr-1" /> No Email
      </Badge>
    );
  }
  
  return (
    <Badge variant="outline" className={`bg-gray-50 text-gray-500 ${small ? 'text-xs' : ''}`}>
      <HelpCircle size={small ? 10 : 12} className="mr-1" /> Unknown
    </Badge>
  );
};

export default function LeadDetail() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [markWonModalOpen, setMarkWonModalOpen] = useState(false);
  const [buildingSite, setBuildingSite] = useState(false);
  
  // Company Intelligence state (Phase 1)
  const [companyIntelligence, setCompanyIntelligence] = useState(null);
  const [loadingIntelligence, setLoadingIntelligence] = useState(false);
  const [activeTab, setActiveTab] = useState("overview"); // overview, intelligence

  useEffect(() => {
    fetchLeadDetails();
  }, [leadId]);

  const fetchLeadDetails = async () => {
    try {
      const leadRes = await axios.get(`${API}/leads/${leadId}`);
      setLead(leadRes.data);

      if (leadRes.data.booking_status === "booked") {
        const apptRes = await axios.get(`${API}/appointments?lead_id=${leadId}`);
        if (apptRes.data.length > 0) {
          setAppointment(apptRes.data[0]);
        }
      }
    } catch (error) {
      console.error("Failed to fetch lead:", error);
      toast.error("Failed to load lead details");
    } finally {
      setLoading(false);
    }
  };
  
  const fetchCompanyIntelligence = async () => {
    setLoadingIntelligence(true);
    try {
      const res = await axios.get(`${API}/leads/${leadId}/company-intelligence`, { withCredentials: true });
      setCompanyIntelligence(res.data.company);
    } catch (error) {
      console.error("Failed to fetch company intelligence:", error);
      toast.error("Failed to load company intelligence");
    } finally {
      setLoadingIntelligence(false);
    }
  };
  
  // Fetch intelligence when switching to intelligence tab
  useEffect(() => {
    if (activeTab === "intelligence" && !companyIntelligence && !loadingIntelligence) {
      fetchCompanyIntelligence();
    }
  }, [activeTab]);

  const updateStatus = async (newStatus) => {
    setUpdating(true);
    try {
      await axios.patch(`${API}/leads/${leadId}/status?status=${newStatus}`);
      setLead(prev => ({ ...prev, status: newStatus }));
      toast.success("Lead status updated");
    } catch (error) {
      console.error("Failed to update status:", error);
      toast.error("Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  const blockLead = async () => {
    setUpdating(true);
    try {
      await axios.post(`${API}/leads/${leadId}/block`);
      setLead(prev => ({ ...prev, suppression_reason: "manual_block" }));
      toast.success("Lead blocked from future campaigns");
    } catch (error) {
      console.error("Failed to block lead:", error);
      toast.error("Failed to block lead");
    } finally {
      setUpdating(false);
    }
  };

  const pollBuildStatus = async () => {
    // Poll for build completion
    const maxAttempts = 60; // 2 minutes max (60 * 2s)
    let attempts = 0;
    
    const poll = async () => {
      try {
        const response = await axios.get(`${API}/leads/${leadId}/build-status`);
        const status = response.data.status;
        
        if (status === "ready") {
          setBuildingSite(false);
          toast.success("Full site built successfully!");
          await fetchLeadDetails();
          return;
        } else if (status === "failed") {
          setBuildingSite(false);
          // Use the error message from the API if available
          const errorMessage = response.data.message || "Build failed. Please try again.";
          toast.error(errorMessage);
          setLead(prev => ({ ...prev, full_site_status: "failed" }));
          return;
        } else if (status === "building") {
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(poll, 2000); // Poll every 2 seconds
          } else {
            setBuildingSite(false);
            toast.error("Build taking too long. Please check back later.");
          }
        }
      } catch (error) {
        console.error("Error polling build status:", error);
        setBuildingSite(false);
        toast.error("Failed to check build status");
      }
    };
    
    await poll();
  };

  const buildFullSite = async (forceRebuild = false) => {
    setBuildingSite(true);
    setLead(prev => ({ ...prev, full_site_status: "building" }));
    toast.info("Building full site... This may take a minute.");
    
    try {
      const response = await axios.post(
        `${API}/leads/${leadId}/build-full-site?force_rebuild=${forceRebuild}`
      );
      
      if (response.data.skipped_rebuild) {
        setBuildingSite(false);
        toast.success("Site already built with current inputs!");
        await fetchLeadDetails();
        return;
      }
      
      if (response.data.status === "building") {
        // Start polling for completion
        pollBuildStatus();
      } else if (response.data.status === "ready") {
        setBuildingSite(false);
        toast.success("Full site built successfully!");
        await fetchLeadDetails();
      }
    } catch (error) {
      console.error("Failed to build full site:", error);
      toast.error(error.response?.data?.detail || "Failed to build full site");
      setLead(prev => ({ ...prev, full_site_status: "failed" }));
      setBuildingSite(false);
    }
  };

  const downloadFullSite = () => {
    window.open(`${API}/leads/${leadId}/download-site`, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="page-content">
        <div className="empty-state">
          <Building2 className="empty-state-icon" />
          <h3 className="empty-state-title">Lead not found</h3>
          <Link to={`${ADMIN_BASE}/leads`}>
            <Button variant="outline" className="mt-4">Back to Leads</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Determine source type and get appropriate status config
  const isOneOff = lead.source_type === 'one_off' || lead.source_type === 'portal';
  const statusConfig = getStatusConfig(lead.source_type);
  const status = statusConfig[lead.status] || statusConfig.sourced || { label: lead.status, className: "status-badge bg-muted text-muted-foreground" };
  
  // For one-off builds, "approved" is equivalent to "won" for full site eligibility
  const canBuildFullSite = isOneOff 
    ? lead.status === 'approved' 
    : lead.status === 'won';
  
  const fastActionExpired = lead.fast_action_expires_at && 
    new Date(lead.fast_action_expires_at) < new Date();
  const isSkipped = !!lead.skipped_reason;

  return (
    <div data-testid="lead-detail-page">
      <div className="page-header">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate("/admin/leads")}
            data-testid="back-btn"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="page-title">{lead.business_name}</h1>
            <p className="page-subtitle">
              {lead.city}, {lead.state} • {lead.primary_industry || lead.category}
              {isOneOff && <span className="ml-2 text-xs bg-accent/20 text-accent px-2 py-0.5 rounded">Single Build</span>}
            </p>
          </div>
        </div>
      </div>
      
      <div className="page-content">
        {/* Skipped Lead Warning */}
        {isSkipped && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertTriangle size={20} className="text-yellow-600 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-800">This lead was skipped</p>
              <p className="text-sm text-yellow-700 mt-1">{lead.skipped_reason}</p>
              {lead.suppressed_from_campaign_id && (
                <p className="text-xs text-yellow-600 mt-2">
                  Original campaign: {lead.suppressed_from_campaign_id.slice(0, 8)}...
                </p>
              )}
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 border-b border-border">
          <button
            onClick={() => setActiveTab("overview")}
            className={`px-4 py-2 font-medium text-sm transition-colors relative ${
              activeTab === "overview"
                ? "text-primary"
                : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid="tab-overview"
          >
            <div className="flex items-center gap-2">
              <Building2 size={16} />
              Overview
            </div>
            {activeTab === "overview" && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
            )}
          </button>
          <button
            onClick={() => setActiveTab("intelligence")}
            className={`px-4 py-2 font-medium text-sm transition-colors relative ${
              activeTab === "intelligence"
                ? "text-primary"
                : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid="tab-intelligence"
          >
            <div className="flex items-center gap-2">
              <Brain size={16} />
              Intelligence
              {companyIntelligence?.analysis_status === "analyzed" && (
                <Badge variant="secondary" className="text-xs py-0 px-1.5">Ready</Badge>
              )}
            </div>
            {activeTab === "intelligence" && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
            )}
          </button>
        </div>

        {/* Intelligence Tab Content */}
        {activeTab === "intelligence" && (
          <div className="space-y-6 animate-fade-in" data-testid="intelligence-tab">
            {loadingIntelligence ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={24} className="animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">Loading company intelligence...</span>
              </div>
            ) : companyIntelligence ? (
              <>
                {/* Research Status Banner with Completeness */}
                <div className={`p-4 rounded-lg border ${
                  companyIntelligence.research_status === "researched" 
                    ? "bg-green-50 border-green-200"
                    : companyIntelligence.research_status === "researching"
                    ? "bg-blue-50 border-blue-200"
                    : companyIntelligence.research_status === "failed"
                    ? "bg-red-50 border-red-200"
                    : "bg-amber-50 border-amber-200"
                }`}>
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-3">
                      {companyIntelligence.research_status === "researched" ? (
                        <CheckCircle size={20} className="text-green-600" />
                      ) : companyIntelligence.research_status === "researching" ? (
                        <Loader2 size={20} className="text-blue-600 animate-spin" />
                      ) : companyIntelligence.research_status === "failed" ? (
                        <XCircle size={20} className="text-red-600" />
                      ) : (
                        <HelpCircle size={20} className="text-amber-600" />
                      )}
                      <div>
                        <p className="font-medium capitalize">
                          {companyIntelligence.research_status === "not_researched" 
                            ? "Not Yet Researched" 
                            : companyIntelligence.research_status?.replace("_", " ")}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {companyIntelligence.research_completed_at && (
                            <span className="text-xs text-muted-foreground">
                              Researched: {new Date(companyIntelligence.research_completed_at).toLocaleDateString()}
                            </span>
                          )}
                          {companyIntelligence.crawl_tier && (
                            <Badge variant="outline" className="text-xs">
                              Tier: {companyIntelligence.crawl_tier === "http" ? "HTTP" : 
                                     companyIntelligence.crawl_tier === "playwright" ? "Browser" : 
                                     companyIntelligence.crawl_tier === "provider_only" ? "Provider Only" : 
                                     companyIntelligence.crawl_tier}
                            </Badge>
                          )}
                        </div>
                        {companyIntelligence.research_error && (
                          <p className="text-sm text-red-600 mt-1">{companyIntelligence.research_error}</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Research Controls */}
                    <div className="flex gap-2 flex-wrap">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={async () => {
                          try {
                            const res = await axios.post(
                              `${API}/companies/${companyIntelligence.company_id}/research/sync`,
                              {},
                              { withCredentials: true }
                            );
                            toast.success(`Research completed - Fit Score: ${res.data.fit_score || 'N/A'}`);
                            fetchCompanyIntelligence();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Research failed");
                          }
                        }}
                        disabled={companyIntelligence.research_status === "researching"}
                        data-testid="research-company-btn"
                      >
                        {companyIntelligence.research_status === "researching" ? (
                          <><Loader2 size={14} className="mr-1 animate-spin" /> Researching...</>
                        ) : companyIntelligence.research_status === "researched" ? (
                          <><RefreshCw size={14} className="mr-1" /> Re-run Research</>
                        ) : (
                          <><Brain size={14} className="mr-1" /> Research Company</>
                        )}
                      </Button>
                      
                      {companyIntelligence.research_status === "researched" && (
                        <>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={async () => {
                              try {
                                const res = await axios.post(
                                  `${API}/companies/${companyIntelligence.company_id}/recalculate-fit`,
                                  {},
                                  { withCredentials: true }
                                );
                                toast.success(`Fit Score recalculated: ${res.data.total_score}/100`);
                                fetchCompanyIntelligence();
                              } catch (err) {
                                toast.error("Failed to recalculate fit score");
                              }
                            }}
                            data-testid="recalculate-fit-btn"
                          >
                            <BarChart3 size={14} className="mr-1" /> Recalculate Fit
                          </Button>
                          
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={async () => {
                              try {
                                const res = await axios.post(
                                  `${API}/companies/${companyIntelligence.company_id}/enrich-contacts`,
                                  {},
                                  { withCredentials: true }
                                );
                                if (res.data.success) {
                                  toast.success(`Found ${res.data.contacts_found} contacts`);
                                  fetchCompanyIntelligence();
                                } else if (res.data.setup_required) {
                                  toast.error(res.data.error || "Apollo not configured");
                                } else {
                                  toast.error(res.data.error || "No contacts found");
                                }
                              } catch (err) {
                                toast.error(err.response?.data?.detail || "Contact enrichment failed");
                              }
                            }}
                            data-testid="enrich-contacts-btn"
                          >
                            <UserCheck size={14} className="mr-1" /> Enrich Contacts
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Fit Score + Research Completeness Summary */}
                {companyIntelligence.research_status === "researched" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Fit Score Card */}
                    <div className="p-4 rounded-lg border bg-card">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-muted-foreground">Stock & Load Fit</span>
                        <Badge 
                          variant="secondary"
                          className={`${
                            companyIntelligence.fit_score?.total_score >= 80 ? "bg-green-100 text-green-800" :
                            companyIntelligence.fit_score?.total_score >= 65 ? "bg-blue-100 text-blue-800" :
                            companyIntelligence.fit_score?.total_score >= 45 ? "bg-amber-100 text-amber-800" :
                            "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {companyIntelligence.fit_score?.total_score >= 80 ? "High Fit" :
                           companyIntelligence.fit_score?.total_score >= 65 ? "Good Fit" :
                           companyIntelligence.fit_score?.total_score >= 45 ? "Possible Fit" :
                           "Low Fit"}
                        </Badge>
                      </div>
                      <div className="text-3xl font-bold">
                        {companyIntelligence.fit_score?.total_score ?? "—"}<span className="text-lg text-muted-foreground">/100</span>
                      </div>
                      {companyIntelligence.fit_score?.combination_bonus > 0 && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Includes +{companyIntelligence.fit_score.combination_bonus} combo bonus
                        </p>
                      )}
                    </div>

                    {/* Research Completeness Card */}
                    <div className="p-4 rounded-lg border bg-card">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-muted-foreground">Research Confidence</span>
                        <Badge 
                          variant="secondary"
                          className={`${
                            companyIntelligence.research_completeness?.completeness_level === "high" ? "bg-green-100 text-green-800" :
                            companyIntelligence.research_completeness?.completeness_level === "moderate" ? "bg-blue-100 text-blue-800" :
                            companyIntelligence.research_completeness?.completeness_level === "low" ? "bg-amber-100 text-amber-800" :
                            "bg-red-100 text-red-800"
                          }`}
                        >
                          {companyIntelligence.research_completeness?.completeness_level?.charAt(0).toUpperCase() + 
                           companyIntelligence.research_completeness?.completeness_level?.slice(1) || "Unknown"}
                        </Badge>
                      </div>
                      <div className="text-3xl font-bold">
                        {companyIntelligence.research_completeness?.completeness_score ?? "—"}<span className="text-lg text-muted-foreground">%</span>
                      </div>
                      {companyIntelligence.research_completeness?.summary && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {companyIntelligence.research_completeness.summary}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Research Completeness Details (if low) */}
                {companyIntelligence.research_completeness?.gaps?.length > 0 && 
                 companyIntelligence.research_completeness?.completeness_level !== "high" && (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-sm font-medium text-amber-800 mb-2">⚠️ Research Gaps</p>
                    <ul className="text-sm text-amber-700 list-disc list-inside space-y-1">
                      {companyIntelligence.research_completeness.gaps.map((gap, idx) => (
                        <li key={idx}>{gap}</li>
                      ))}
                    </ul>
                    <p className="text-xs text-amber-600 mt-2 italic">
                      Note: Low research confidence does not mean low fit. It means we need more data to be certain.
                    </p>
                  </div>
                )}

                {/* Contact Enrichment Status */}
                {companyIntelligence.contact_enrichment_status && (
                  <div className={`p-3 rounded-lg border ${
                    companyIntelligence.contact_enrichment_status.configured 
                      ? "bg-blue-50 border-blue-200" 
                      : "bg-gray-50 border-gray-200"
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <UserCheck size={16} className={companyIntelligence.contact_enrichment_status.configured ? "text-blue-600" : "text-gray-400"} />
                        <span className="text-sm font-medium">
                          Contact Enrichment: {companyIntelligence.contact_enrichment_status.provider}
                        </span>
                      </div>
                      {companyIntelligence.contact_enrichment_status.configured ? (
                        <Badge variant="secondary" className="bg-green-100 text-green-800">Configured</Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-gray-100 text-gray-600">Not Configured</Badge>
                      )}
                    </div>
                    {!companyIntelligence.contact_enrichment_status.configured && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {companyIntelligence.contact_enrichment_status.status_message}
                      </p>
                    )}
                    {/* Only show Apollo contacts when configured AND found */}
                    {companyIntelligence.contact_enrichment_status.configured && 
                     companyIntelligence.contact_enrichment_status.apollo_contacts_found > 0 && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {companyIntelligence.contact_enrichment_status.apollo_contacts_found} contact{companyIntelligence.contact_enrichment_status.apollo_contacts_found !== 1 ? 's' : ''} found via Apollo
                      </p>
                    )}
                    {/* Warning for test data */}
                    {companyIntelligence.contact_enrichment_status.test_data_contacts > 0 && (
                      <p className="text-xs text-amber-600 mt-1 font-medium">
                        ⚠️ {companyIntelligence.contact_enrichment_status.test_data_contacts} TEST DATA contact{companyIntelligence.contact_enrichment_status.test_data_contacts !== 1 ? 's' : ''} (not real)
                      </p>
                    )}
                  </div>
                )}

                {/* Apollo Organization Data */}
                {companyIntelligence.apollo_organization && (
                  <CollapsiblePanel
                    id="apollo-org"
                    title="Apollo Organization Data"
                    icon={Building2}
                    defaultOpen={false}
                    defaultOpenMobile={false}
                    badge={
                      <Badge variant="secondary" className="ml-2 bg-purple-100 text-purple-800">
                        {companyIntelligence.apollo_organization.match_confidence >= 0.9 ? "High Match" : 
                         companyIntelligence.apollo_organization.match_confidence >= 0.7 ? "Good Match" : "Partial Match"}
                      </Badge>
                    }
                  >
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Company Name</p>
                          <p className="font-medium">{companyIntelligence.apollo_organization.name || "—"}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Industry</p>
                          <p className="font-medium">{companyIntelligence.apollo_organization.industry || "—"}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Employees</p>
                          <p className="font-medium">
                            {companyIntelligence.apollo_organization.estimated_employees?.toLocaleString() || "—"}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Annual Revenue</p>
                          <p className="font-medium">{companyIntelligence.apollo_organization.annual_revenue_printed || "—"}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Location</p>
                          <p className="font-medium">
                            {companyIntelligence.apollo_organization.city && companyIntelligence.apollo_organization.state
                              ? `${companyIntelligence.apollo_organization.city}, ${companyIntelligence.apollo_organization.state}`
                              : "—"}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Founded</p>
                          <p className="font-medium">{companyIntelligence.apollo_organization.founded_year || "—"}</p>
                        </div>
                      </div>
                      
                      {/* Social Links */}
                      <div className="flex flex-wrap gap-2 pt-2 border-t">
                        {companyIntelligence.apollo_organization.linkedin_url && (
                          <a 
                            href={companyIntelligence.apollo_organization.linkedin_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                          >
                            <ExternalLink size={12} /> LinkedIn
                          </a>
                        )}
                        {companyIntelligence.apollo_organization.facebook_url && (
                          <a 
                            href={companyIntelligence.apollo_organization.facebook_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                          >
                            <ExternalLink size={12} /> Facebook
                          </a>
                        )}
                        {companyIntelligence.apollo_organization.phone && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Phone size={12} /> {companyIntelligence.apollo_organization.phone}
                          </span>
                        )}
                      </div>
                      
                      {/* Growth Signals */}
                      {companyIntelligence.apollo_organization.headcount_growth_12mo && (
                        <div className="pt-2 border-t">
                          <p className="text-xs text-muted-foreground mb-1">12-Month Headcount Growth</p>
                          <Badge 
                            variant="secondary"
                            className={companyIntelligence.apollo_organization.headcount_growth_12mo > 0 
                              ? "bg-green-100 text-green-800" 
                              : "bg-red-100 text-red-800"}
                          >
                            {(companyIntelligence.apollo_organization.headcount_growth_12mo * 100).toFixed(1)}%
                          </Badge>
                        </div>
                      )}
                      
                      <p className="text-xs text-muted-foreground pt-2 border-t">
                        Apollo ID: {companyIntelligence.apollo_organization.apollo_organization_id} | 
                        Credits: {companyIntelligence.apollo_organization.credits_consumed}
                      </p>
                    </div>
                  </CollapsiblePanel>
                )}

                {/* Franchise Information */}
                {companyIntelligence.franchise_info && companyIntelligence.franchise_info.company_type !== "unknown" && (
                  <div className={`p-3 rounded-lg border ${
                    companyIntelligence.franchise_info.company_type === "local_franchise" 
                      ? "bg-orange-50 border-orange-200"
                      : companyIntelligence.franchise_info.company_type === "franchise_brand"
                      ? "bg-purple-50 border-purple-200"
                      : "bg-gray-50 border-gray-200"
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Building2 size={16} className="text-muted-foreground" />
                        <span className="text-sm font-medium">Company Structure</span>
                      </div>
                      <Badge 
                        variant="secondary"
                        className={`${
                          companyIntelligence.franchise_info.company_type === "local_franchise" 
                            ? "bg-orange-100 text-orange-800"
                            : companyIntelligence.franchise_info.company_type === "franchise_brand"
                            ? "bg-purple-100 text-purple-800"
                            : companyIntelligence.franchise_info.company_type === "corporate_location"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {companyIntelligence.franchise_info.company_type === "local_franchise" ? "Local Franchise" :
                         companyIntelligence.franchise_info.company_type === "franchise_brand" ? "Franchise Brand" :
                         companyIntelligence.franchise_info.company_type === "corporate_location" ? "Corporate Location" :
                         "Independent Company"}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {companyIntelligence.franchise_info.determination_reason}
                    </p>
                    {companyIntelligence.franchise_info.local_territory && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Territory: {companyIntelligence.franchise_info.local_territory}
                      </p>
                    )}
                    {companyIntelligence.franchise_info.franchise_signals?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {companyIntelligence.franchise_info.franchise_signals.slice(0, 3).map((signal, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs font-normal">
                            {signal}
                          </Badge>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground mt-2 italic">
                      Note: For Stock & Load, local operating entities are often the correct prospect.
                    </p>
                  </div>
                )}

                {/* Sales Brief (if available) */}
                {companyIntelligence.sales_brief && (
                  <CollapsiblePanel
                    id="sales-brief"
                    title="Sales Intelligence Brief"
                    icon={Lightbulb}
                    defaultOpen={true}
                    defaultOpenMobile={true}
                    badge={
                      companyIntelligence.sales_brief.fit_classification && (
                        <Badge 
                          variant="secondary" 
                          className={`ml-2 ${
                            companyIntelligence.sales_brief.fit_classification === "High Fit" ? "bg-green-100 text-green-800" :
                            companyIntelligence.sales_brief.fit_classification === "Good Fit" ? "bg-blue-100 text-blue-800" :
                            companyIntelligence.sales_brief.fit_classification === "Possible Fit" ? "bg-amber-100 text-amber-800" :
                            "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {companyIntelligence.sales_brief.fit_classification}
                        </Badge>
                      )
                    }
                  >
                    <div className="space-y-4">
                      {companyIntelligence.sales_brief.recommended_angle && (
                        <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
                          <p className="text-sm font-medium text-primary mb-1">Recommended Angle</p>
                          <p className="text-sm">{companyIntelligence.sales_brief.recommended_angle}</p>
                          {companyIntelligence.sales_brief.angle_reasoning && (
                            <p className="text-xs text-muted-foreground mt-1">{companyIntelligence.sales_brief.angle_reasoning}</p>
                          )}
                        </div>
                      )}
                      
                      {companyIntelligence.sales_brief.known_facts?.length > 0 && (
                        <div>
                          <p className="text-sm font-medium mb-2">What We Know (Observed)</p>
                          <div className="flex flex-wrap gap-2">
                            {companyIntelligence.sales_brief.known_facts.map((fact, idx) => (
                              <Badge key={idx} variant="secondary" className="bg-green-50 text-green-700">{fact}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {companyIntelligence.sales_brief.likely_complexity?.length > 0 && (
                        <div>
                          <p className="text-sm font-medium mb-2">Likely Operational Complexity</p>
                          <div className="flex flex-wrap gap-2">
                            {companyIntelligence.sales_brief.likely_complexity.map((item, idx) => (
                              <Badge key={idx} variant="outline">{item}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {companyIntelligence.sales_brief.do_not_claim?.length > 0 && (
                        <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                          <p className="text-sm font-medium text-amber-800 mb-2">⚠️ Do Not Claim (Unverified)</p>
                          <ul className="text-sm text-amber-700 list-disc list-inside">
                            {companyIntelligence.sales_brief.do_not_claim.map((item, idx) => (
                              <li key={idx}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </CollapsiblePanel>
                )}

                {/* Decision Makers - Prospeo Discovery + Legacy Contacts */}
                {(companyIntelligence.discovered_contacts || companyIntelligence.decision_makers?.length > 0) && (
                  <CollapsiblePanel
                    id="decision-maker"
                    title="Decision Makers"
                    icon={UserCheck}
                    defaultOpen={true}
                    defaultOpenMobile={true}
                    badge={
                      <Badge variant="secondary" className="ml-2">
                        {companyIntelligence.discovered_contacts?.total_discovered || companyIntelligence.decision_makers?.length || 0} contact{(companyIntelligence.discovered_contacts?.total_discovered || companyIntelligence.decision_makers?.length || 0) !== 1 ? 's' : ''}
                      </Badge>
                    }
                  >
                    <div className="space-y-4">
                      {/* Prospeo Discovery Section */}
                      {companyIntelligence.discovered_contacts ? (
                        <>
                          {/* Provider Badge */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">
                                Provider: {companyIntelligence.discovered_contacts.provider}
                              </Badge>
                              <Badge variant="secondary" className="text-xs">
                                S:{companyIntelligence.discovered_contacts.tier_distribution?.S || 0} 
                                A:{companyIntelligence.discovered_contacts.tier_distribution?.A || 0} 
                                B:{companyIntelligence.discovered_contacts.tier_distribution?.B || 0}
                              </Badge>
                            </div>
                            <span className="text-xs text-muted-foreground">
                              Discovered: {companyIntelligence.discovered_contacts.discovered_at ? 
                                new Date(companyIntelligence.discovered_contacts.discovered_at).toLocaleDateString() : 'Unknown'}
                            </span>
                          </div>

                          {/* Best Contact Section */}
                          {companyIntelligence.discovered_contacts.best_contact && (
                            <div className="p-4 rounded-lg border-2 border-primary bg-primary/5">
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <Badge className="bg-primary text-primary-foreground">Best Contact</Badge>
                                <TierBadge tier={companyIntelligence.discovered_contacts.best_contact.contact_tier} />
                                <Badge variant="secondary" className="text-xs">
                                  Score: {companyIntelligence.discovered_contacts.best_contact.contact_score}
                                </Badge>
                                <EmploymentStatusBadge status={companyIntelligence.discovered_contacts.best_contact.employment_status} />
                              </div>
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="text-lg font-semibold">{companyIntelligence.discovered_contacts.best_contact.full_name}</span>
                                  </div>
                                  {companyIntelligence.discovered_contacts.best_contact.title && (
                                    <p className="text-sm text-muted-foreground">
                                      {companyIntelligence.discovered_contacts.best_contact.title}
                                    </p>
                                  )}
                                  <div className="flex flex-wrap gap-3 mt-2">
                                    {companyIntelligence.discovered_contacts.best_contact.email ? (
                                      <span className="text-sm text-primary flex items-center gap-1">
                                        <Mail size={14} /> {companyIntelligence.discovered_contacts.best_contact.email}
                                      </span>
                                    ) : (
                                      <EmailStatusBadge 
                                        status={companyIntelligence.discovered_contacts.best_contact.email_status}
                                        personId={companyIntelligence.discovered_contacts.best_contact.prospeo_person_id}
                                        companyId={companyIntelligence.company_id}
                                        onEnrich={() => fetchCompanyIntelligence()}
                                      />
                                    )}
                                    {companyIntelligence.discovered_contacts.best_contact.linkedin_url && (
                                      <a href={companyIntelligence.discovered_contacts.best_contact.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                                        <ExternalLink size={14} /> LinkedIn
                                      </a>
                                    )}
                                    {companyIntelligence.discovered_contacts.best_contact.location && (
                                      <span className="text-sm text-muted-foreground flex items-center gap-1">
                                        <MapPin size={14} /> {companyIntelligence.discovered_contacts.best_contact.location}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <p className="mt-3 text-xs text-muted-foreground border-t pt-2">
                                <strong>Why:</strong> Best operational decision-maker identified based on title, authority, and company context
                              </p>
                              {companyIntelligence.discovered_contacts.best_contact.scoring_reasons?.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {companyIntelligence.discovered_contacts.best_contact.scoring_reasons.slice(0, 3).map((reason, ridx) => (
                                    <Badge key={ridx} variant="outline" className="text-xs font-normal">
                                      {reason}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Best Reachable Contact - Only show if different from Best Contact */}
                          {companyIntelligence.discovered_contacts.best_reachable_contact && 
                           companyIntelligence.discovered_contacts.best_reachable_contact.prospeo_person_id !== 
                           companyIntelligence.discovered_contacts.best_contact?.prospeo_person_id && (
                            <div className="p-4 rounded-lg border border-green-200 bg-green-50/30">
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <Badge className="bg-green-600 text-white">Best Reachable Contact</Badge>
                                <TierBadge tier={companyIntelligence.discovered_contacts.best_reachable_contact.contact_tier} />
                                <Badge variant="secondary" className="text-xs">
                                  Score: {companyIntelligence.discovered_contacts.best_reachable_contact.contact_score}
                                </Badge>
                              </div>
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="text-lg font-semibold">{companyIntelligence.discovered_contacts.best_reachable_contact.full_name}</span>
                                  </div>
                                  {companyIntelligence.discovered_contacts.best_reachable_contact.title && (
                                    <p className="text-sm text-muted-foreground">
                                      {companyIntelligence.discovered_contacts.best_reachable_contact.title}
                                    </p>
                                  )}
                                  <div className="flex flex-wrap gap-3 mt-2">
                                    {companyIntelligence.discovered_contacts.best_reachable_contact.email ? (
                                      <span className="text-sm text-primary flex items-center gap-1">
                                        <Mail size={14} /> {companyIntelligence.discovered_contacts.best_reachable_contact.email}
                                      </span>
                                    ) : (
                                      <EmailStatusBadge 
                                        status={companyIntelligence.discovered_contacts.best_reachable_contact.email_status}
                                        personId={companyIntelligence.discovered_contacts.best_reachable_contact.prospeo_person_id}
                                        companyId={companyIntelligence.company_id}
                                        onEnrich={() => fetchCompanyIntelligence()}
                                      />
                                    )}
                                    {companyIntelligence.discovered_contacts.best_reachable_contact.linkedin_url && (
                                      <a href={companyIntelligence.discovered_contacts.best_reachable_contact.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                                        <ExternalLink size={14} /> LinkedIn
                                      </a>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <p className="mt-3 text-xs text-muted-foreground border-t pt-2">
                                <strong>Why:</strong> Highest-ranked contact with verified email available
                              </p>
                            </div>
                          )}

                          {/* Note when Best Contact IS reachable */}
                          {companyIntelligence.discovered_contacts.best_contact && 
                           companyIntelligence.discovered_contacts.best_reachable_contact?.prospeo_person_id === 
                           companyIntelligence.discovered_contacts.best_contact?.prospeo_person_id && (
                            <p className="text-xs text-green-700 bg-green-50 px-3 py-2 rounded">
                              <CheckCircle2 size={12} className="inline mr-1" />
                              Best Contact also has verified email available
                            </p>
                          )}

                          {/* Alternate Contacts - S/A/B tier */}
                          {companyIntelligence.discovered_contacts.contacts?.filter(c => 
                            !c.is_recommended && ['S', 'A', 'B'].includes(c.contact_tier)
                          ).length > 0 && (
                            <div className="border-t pt-4">
                              <p className="text-sm font-medium text-muted-foreground mb-3">
                                Alternate Contacts ({companyIntelligence.discovered_contacts.contacts.filter(c => !c.is_recommended && ['S', 'A', 'B'].includes(c.contact_tier)).length})
                              </p>
                              <div className="space-y-3">
                                {companyIntelligence.discovered_contacts.contacts
                                  .filter(c => !c.is_recommended && ['S', 'A', 'B'].includes(c.contact_tier))
                                  .slice(0, 10)
                                  .map((contact, idx) => (
                                  <div 
                                    key={contact.prospeo_person_id || `alt-${idx}`}
                                    className="p-3 rounded-lg border border-border bg-muted/30"
                                  >
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <span className="font-medium">{contact.full_name}</span>
                                          <TierBadge tier={contact.contact_tier} small />
                                          <EmploymentStatusBadge status={contact.employment_status} small />
                                        </div>
                                        {contact.title && <p className="text-sm text-muted-foreground">{contact.title}</p>}
                                        <div className="flex flex-wrap gap-2 mt-1">
                                          {contact.email ? (
                                            <span className="text-xs text-primary flex items-center gap-1">
                                              <Mail size={10} /> {contact.email}
                                            </span>
                                          ) : (
                                            <EmailStatusBadge 
                                              status={contact.email_status}
                                              personId={contact.prospeo_person_id}
                                              companyId={companyIntelligence.company_id}
                                              onEnrich={() => fetchCompanyIntelligence()}
                                              small
                                            />
                                          )}
                                          {contact.linkedin_url && (
                                            <a href={contact.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                                              <ExternalLink size={10} /> LinkedIn
                                            </a>
                                          )}
                                        </div>
                                      </div>
                                      <div className="text-right">
                                        <Badge variant="secondary" className="text-xs">
                                          {contact.contact_score} pts
                                        </Badge>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* C/D tier contacts collapsed */}
                          {companyIntelligence.discovered_contacts.contacts?.filter(c => 
                            ['C', 'D'].includes(c.contact_tier)
                          ).length > 0 && (
                            <details className="border-t pt-4">
                              <summary className="text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground">
                                Lower Priority Contacts ({companyIntelligence.discovered_contacts.contacts.filter(c => ['C', 'D'].includes(c.contact_tier)).length})
                              </summary>
                              <div className="mt-3 space-y-2">
                                {companyIntelligence.discovered_contacts.contacts
                                  .filter(c => ['C', 'D'].includes(c.contact_tier))
                                  .slice(0, 10)
                                  .map((contact, idx) => (
                                  <div 
                                    key={contact.prospeo_person_id || `low-${idx}`}
                                    className="p-2 rounded border border-muted bg-muted/10 text-sm"
                                  >
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <span className="font-medium">{contact.full_name}</span>
                                        <span className="text-muted-foreground ml-2">— {contact.title}</span>
                                      </div>
                                      <TierBadge tier={contact.contact_tier} small />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </details>
                          )}

                          {/* Credits consumed */}
                          {companyIntelligence.discovered_contacts.credits_consumed && (
                            <div className="text-xs text-muted-foreground border-t pt-2 mt-2">
                              Credits: {companyIntelligence.discovered_contacts.credits_consumed.search_credits || 0} search + {companyIntelligence.discovered_contacts.credits_consumed.enrichment_credits || 0} enrichment
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          {/* Legacy Decision Makers Display */}
                          {/* Test Data Warning */}
                          {companyIntelligence.decision_makers?.some(dm => dm.source?.startsWith('test_data:')) && (
                            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                              <p className="text-sm text-amber-800 font-medium">⚠️ TEST DATA Present</p>
                              <p className="text-xs text-amber-700">Some contacts below are test/seed data and do not represent real people.</p>
                            </div>
                          )}

                          {/* Recommended Contact */}
                          {companyIntelligence.decision_makers?.filter(d => d.is_primary).map((dm, idx) => (
                            <div 
                              key={dm.contact_id || `primary-${idx}`}
                              className={`p-4 rounded-lg border-2 ${
                                dm.source?.startsWith('test_data:') 
                                  ? "border-amber-300 bg-amber-50" 
                                  : "border-primary bg-primary/5"
                              }`}
                            >
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                {dm.source?.startsWith('test_data:') ? (
                                  <Badge className="bg-amber-500 text-white">TEST DATA</Badge>
                                ) : (
                                  <Badge className="bg-primary text-primary-foreground">Recommended Contact</Badge>
                                )}
                                <Badge 
                                  variant="secondary"
                                  className={`${
                                    dm.confidence_level === "verified" ? "bg-green-100 text-green-800" :
                                    dm.confidence_level === "likely" ? "bg-blue-100 text-blue-800" :
                                    dm.confidence_level === "possible" ? "bg-amber-100 text-amber-800" :
                                    "bg-gray-100 text-gray-800"
                                  }`}
                                >
                                  {dm.confidence_score}% confidence
                                </Badge>
                                {dm.source && (
                                  <Badge variant="outline" className="text-xs">
                                    {dm.source.replace('provider:', '').replace('lead_data:', 'Lead: ').replace('test_data:', 'Test: ')}
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-start justify-between">
                                <div>
                                  <div className="flex items-center gap-2">
                                    {dm.name && <span className="text-lg font-semibold">{dm.name}</span>}
                                  </div>
                                  {dm.title && (
                                    <p className="text-sm text-muted-foreground">
                                      {dm.title}
                                      {dm.role_family && (
                                        <span className="ml-2 text-xs bg-muted px-1.5 py-0.5 rounded">
                                          {dm.role_family.replace(/_/g, ' ')}
                                        </span>
                                      )}
                                    </p>
                                  )}
                                  <div className="flex flex-wrap gap-3 mt-2">
                                    {dm.email && (
                                      <span className={`text-sm flex items-center gap-1 ${dm.source?.startsWith('test_data:') ? 'text-amber-600 line-through' : 'text-primary hover:underline'}`}>
                                        <Mail size={14} /> {dm.email}
                                        {dm.source?.startsWith('test_data:') && <span className="text-xs">(test)</span>}
                                      </span>
                                    )}
                                    {dm.phone && !dm.source?.startsWith('test_data:') && (
                                      <a href={`tel:${dm.phone}`} className="text-sm text-muted-foreground flex items-center gap-1">
                                        <Phone size={14} /> {dm.phone}
                                      </a>
                                    )}
                                    {dm.linkedin_url && (
                                      <a href={dm.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                                        <ExternalLink size={14} /> LinkedIn
                                      </a>
                                    )}
                                  </div>
                                </div>
                              </div>
                              {dm.recommendation_reason && !dm.source?.startsWith('test_data:') && (
                                <p className="mt-3 text-xs text-muted-foreground border-t pt-2">
                                  <strong>Why recommended:</strong> {dm.recommendation_reason}
                                </p>
                              )}
                              {dm.confidence_reasons?.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {dm.confidence_reasons.map((reason, ridx) => (
                                    <Badge key={ridx} variant="outline" className={`text-xs font-normal ${reason.includes('TEST DATA') ? 'bg-amber-100 border-amber-300' : ''}`}>
                                      {reason}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}

                          {/* Alternate Contacts */}
                          {companyIntelligence.decision_makers?.filter(d => !d.is_primary).length > 0 && (
                            <div className="border-t pt-4">
                              <p className="text-sm font-medium text-muted-foreground mb-3">
                                Alternate Contacts ({companyIntelligence.decision_makers.filter(d => !d.is_primary).length})
                              </p>
                              <div className="space-y-3">
                                {companyIntelligence.decision_makers.filter(d => !d.is_primary).map((dm, idx) => (
                                  <div 
                                    key={dm.contact_id || `alt-${idx}`}
                                    className={`p-3 rounded-lg border ${
                                      dm.source?.startsWith('test_data:') 
                                        ? "border-amber-200 bg-amber-50/50" 
                                        : "border-border bg-muted/30"
                                    }`}
                                  >
                                    <div className="flex items-start justify-between">
                                      <div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                          {dm.name && <span className="font-medium">{dm.name}</span>}
                                          {dm.source?.startsWith('test_data:') && (
                                            <Badge className="bg-amber-500 text-white text-xs">TEST</Badge>
                                          )}
                                          {dm.role_family && !dm.source?.startsWith('test_data:') && (
                                            <Badge variant="outline" className="text-xs">
                                              {dm.role_family.replace(/_/g, ' ')}
                                            </Badge>
                                          )}
                                        </div>
                                        {dm.title && <p className="text-sm text-muted-foreground">{dm.title}</p>}
                                        <div className="flex flex-wrap gap-2 mt-1">
                                          {dm.email && (
                                            <span className={`text-xs flex items-center gap-1 ${dm.source?.startsWith('test_data:') ? 'text-amber-600 line-through' : 'text-primary hover:underline'}`}>
                                              <Mail size={10} /> {dm.email}
                                            </span>
                                          )}
                                          {dm.phone && !dm.source?.startsWith('test_data:') && (
                                            <a href={`tel:${dm.phone}`} className="text-xs text-muted-foreground flex items-center gap-1">
                                              <Phone size={10} /> {dm.phone}
                                            </a>
                                          )}
                                          {dm.linkedin_url && (
                                            <a href={dm.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                                              <ExternalLink size={10} /> LinkedIn
                                            </a>
                                          )}
                                        </div>
                                      </div>
                                      <div className="text-right">
                                        <Badge 
                                          variant="secondary"
                                          className={`text-xs ${
                                            dm.confidence_level === "verified" ? "bg-green-100 text-green-800" :
                                            dm.confidence_level === "likely" ? "bg-blue-100 text-blue-800" :
                                            dm.confidence_level === "possible" ? "bg-amber-100 text-amber-800" :
                                            "bg-gray-100 text-gray-800"
                                          }`}
                                        >
                                          {dm.confidence_score}%
                                        </Badge>
                                        <p className="text-xs text-muted-foreground mt-0.5 capitalize">{dm.confidence_level}</p>
                                      </div>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">
                                      Source: {dm.source?.replace('provider:', '').replace('lead_data:', 'Lead: ').replace('test_data:', 'Test: ')}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* No contacts message */}
                          {(!companyIntelligence.decision_makers || companyIntelligence.decision_makers.length === 0) && (
                            <div className="text-center text-muted-foreground py-4">
                              No decision makers identified yet. Try running contact discovery.
                            </div>
                          )}
                        </>
                      )}

                      {/* Discover Contacts Button */}
                      {!companyIntelligence.discovered_contacts && companyIntelligence.identity?.domain && (
                        <div className="pt-4 border-t">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              try {
                                toast.loading("Discovering contacts...");
                                const res = await axios.post(
                                  `${API}/companies/${companyIntelligence.company_id}/discover-contacts`,
                                  {},
                                  { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
                                );
                                toast.dismiss();
                                toast.success(`Found ${res.data.total_discovered} contacts`);
                                fetchCompanyIntelligence();
                              } catch (err) {
                                toast.dismiss();
                                toast.error(err.response?.data?.detail || "Contact discovery failed");
                              }
                            }}
                            data-testid="discover-contacts-btn"
                          >
                            <Users size={14} className="mr-1" /> Discover Contacts (Prospeo)
                          </Button>
                        </div>
                      )}
                    </div>
                  </CollapsiblePanel>
                )}

                {/* Stock & Load Fit Score */}
                <CollapsiblePanel
                  id="fit-score"
                  title="Stock & Load Fit Score"
                  icon={BarChart3}
                  defaultOpen={true}
                  defaultOpenMobile={true}
                  badge={
                    companyIntelligence.fit_score?.total_score !== null && companyIntelligence.fit_score?.total_score !== undefined ? (
                      <Badge variant="secondary" className="ml-2 text-lg font-bold">
                        {companyIntelligence.fit_score.total_score}/100
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="ml-2 bg-muted">Not calculated</Badge>
                    )
                  }
                >
                  <div className="space-y-4">
                    {companyIntelligence.fit_score?.dimensions?.map((dim) => (
                      <div key={dim.dimension_key} className="space-y-1">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {dim.dimension_key === "field_service" && <Truck size={14} className="text-muted-foreground" />}
                            {dim.dimension_key === "crm_customer" && <Users size={14} className="text-muted-foreground" />}
                            {dim.dimension_key === "workforce_crew" && <Users size={14} className="text-muted-foreground" />}
                            {dim.dimension_key === "project_job" && <Briefcase size={14} className="text-muted-foreground" />}
                            {dim.dimension_key === "materials_inventory" && <Package size={14} className="text-muted-foreground" />}
                            {dim.dimension_key === "growth_organizational" && <TrendingUp size={14} className="text-muted-foreground" />}
                            <span className="text-sm font-medium">{dim.dimension_name}</span>
                          </div>
                          <span className="text-sm">
                            {dim.score !== null && dim.score !== undefined ? (
                              <span className="font-semibold">{dim.score}</span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                            <span className="text-muted-foreground">/{dim.max_points}</span>
                          </span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-primary transition-all"
                            style={{ 
                              width: dim.score !== null && dim.score !== undefined 
                                ? `${(dim.score / dim.max_points) * 100}%` 
                                : '0%' 
                            }}
                          />
                        </div>
                        {dim.signals && dim.signals.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {dim.signals.map((signal, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {signal}
                              </Badge>
                            ))}
                          </div>
                        )}
                        {/* Show detailed reasons if available (Phase 2) */}
                        {dim.reasons && dim.reasons.length > 0 && (
                          <div className="mt-2 text-xs text-muted-foreground space-y-0.5">
                            {dim.reasons.map((reason, idx) => (
                              <div key={idx} className="flex items-center gap-1">
                                <CheckCircle size={10} className="text-green-600" />
                                <span>{reason}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                    
                    {companyIntelligence.fit_score?.calculated_at && (
                      <p className="text-xs text-muted-foreground pt-2 border-t">
                        Calculated: {new Date(companyIntelligence.fit_score.calculated_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                </CollapsiblePanel>

                {/* Company Identity */}
                <CollapsiblePanel
                  id="company-identity"
                  title="Company Identity"
                  icon={Building2}
                  defaultOpen={true}
                  defaultOpenMobile={false}
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {companyIntelligence.identity?.legal_name && (
                      <div>
                        <p className="text-sm text-muted-foreground">Legal Name</p>
                        <p className="font-medium">{companyIntelligence.identity.legal_name}</p>
                      </div>
                    )}
                    {companyIntelligence.identity?.dba_names?.length > 0 && (
                      <div>
                        <p className="text-sm text-muted-foreground">DBA Names</p>
                        <p className="font-medium">{companyIntelligence.identity.dba_names.join(", ")}</p>
                      </div>
                    )}
                    {companyIntelligence.identity?.primary_phone && (
                      <div>
                        <p className="text-sm text-muted-foreground">Phone</p>
                        <p className="font-medium">{companyIntelligence.identity.primary_phone}</p>
                      </div>
                    )}
                    {companyIntelligence.identity?.website && (
                      <div>
                        <p className="text-sm text-muted-foreground">Website</p>
                        <a href={companyIntelligence.identity.website} target="_blank" rel="noopener noreferrer" 
                           className="font-medium text-primary hover:underline flex items-center gap-1">
                          {companyIntelligence.identity.website}
                          <ExternalLink size={12} />
                        </a>
                      </div>
                    )}
                    {(companyIntelligence.identity?.city || companyIntelligence.identity?.state) && (
                      <div>
                        <p className="text-sm text-muted-foreground">Location</p>
                        <p className="font-medium">
                          {[companyIntelligence.identity.city, companyIntelligence.identity.state].filter(Boolean).join(", ")}
                        </p>
                      </div>
                    )}
                  </div>
                </CollapsiblePanel>

                {/* Size Indicators */}
                <CollapsiblePanel
                  id="size-indicators"
                  title="Size Indicators"
                  icon={Users}
                  defaultOpen={false}
                  defaultOpenMobile={false}
                >
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {companyIntelligence.size_indicators?.employee_count_estimate && (
                      <div className="text-center p-4 bg-muted rounded-lg">
                        <p className="text-2xl font-bold">{companyIntelligence.size_indicators.employee_count_estimate}</p>
                        <p className="text-sm text-muted-foreground">Est. Employees</p>
                      </div>
                    )}
                    {companyIntelligence.size_indicators?.truck_count_estimate && (
                      <div className="text-center p-4 bg-muted rounded-lg">
                        <p className="text-2xl font-bold">{companyIntelligence.size_indicators.truck_count_estimate}</p>
                        <p className="text-sm text-muted-foreground">Est. Trucks</p>
                      </div>
                    )}
                    {companyIntelligence.size_indicators?.revenue_range && (
                      <div className="text-center p-4 bg-muted rounded-lg">
                        <p className="text-2xl font-bold">{companyIntelligence.size_indicators.revenue_range}</p>
                        <p className="text-sm text-muted-foreground">Revenue Range</p>
                      </div>
                    )}
                    {!companyIntelligence.size_indicators?.employee_count_estimate && 
                     !companyIntelligence.size_indicators?.truck_count_estimate &&
                     !companyIntelligence.size_indicators?.revenue_range && (
                      <div className="col-span-3 text-center text-muted-foreground py-4">
                        No size data available yet
                      </div>
                    )}
                  </div>
                </CollapsiblePanel>

                {/* Operating Model */}
                <CollapsiblePanel
                  id="operating-model"
                  title="Operating Model"
                  icon={Briefcase}
                  defaultOpen={false}
                  defaultOpenMobile={false}
                >
                  <div className="space-y-4">
                    {companyIntelligence.operating_model?.primary_services?.length > 0 && (
                      <div>
                        <p className="text-sm text-muted-foreground mb-2">Primary Services</p>
                        <div className="flex flex-wrap gap-2">
                          {companyIntelligence.operating_model.primary_services.map((svc, idx) => (
                            <Badge key={idx} variant="secondary">{svc}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {companyIntelligence.operating_model?.trades?.length > 0 && (
                      <div>
                        <p className="text-sm text-muted-foreground mb-2">Trades</p>
                        <div className="flex flex-wrap gap-2">
                          {companyIntelligence.operating_model.trades.map((trade, idx) => (
                            <Badge key={idx} variant="outline">{trade}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {companyIntelligence.operating_model?.business_model && (
                      <div>
                        <p className="text-sm text-muted-foreground">Business Model</p>
                        <p className="font-medium capitalize">{companyIntelligence.operating_model.business_model}</p>
                      </div>
                    )}
                    {!companyIntelligence.operating_model?.primary_services?.length &&
                     !companyIntelligence.operating_model?.trades?.length &&
                     !companyIntelligence.operating_model?.business_model && (
                      <div className="text-center text-muted-foreground py-4">
                        No operating model data available yet
                      </div>
                    )}
                  </div>
                </CollapsiblePanel>

                {/* Evidence */}
                <CollapsiblePanel
                  id="evidence"
                  title="Evidence & Sources"
                  icon={Eye}
                  defaultOpen={false}
                  defaultOpenMobile={false}
                  badge={
                    companyIntelligence.evidence?.length > 0 && (
                      <Badge variant="secondary" className="ml-2">{companyIntelligence.evidence.length}</Badge>
                    )
                  }
                >
                  {companyIntelligence.evidence?.length > 0 ? (
                    <div className="space-y-3">
                      {companyIntelligence.evidence.map((ev) => (
                        <div key={ev.evidence_id} className="p-3 border rounded-lg">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm">{ev.field_name}</span>
                                <Badge 
                                  variant="outline" 
                                  className={ev.evidence_type === "observed" ? "text-green-700 border-green-300" : "text-blue-700 border-blue-300"}
                                >
                                  {ev.evidence_type === "observed" ? (
                                    <><Eye size={10} className="mr-1" /> Observed</>
                                  ) : (
                                    <><Lightbulb size={10} className="mr-1" /> Inferred</>
                                  )}
                                </Badge>
                              </div>
                              <p className="text-sm mt-1">
                                <span className="text-muted-foreground">Value:</span>{" "}
                                <span className="font-medium">{String(ev.value)}</span>
                              </p>
                            </div>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <div className="text-right">
                                    <p className="text-xs text-muted-foreground">{ev.source}</p>
                                    <p className="text-xs font-medium">{Math.round(ev.confidence * 100)}% conf</p>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Source: {ev.source}</p>
                                  <p>Confidence: {Math.round(ev.confidence * 100)}%</p>
                                  {ev.supporting_text && <p className="mt-1 text-xs">{ev.supporting_text}</p>}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          {ev.supporting_text && (
                            <p className="text-xs text-muted-foreground mt-2 italic">&ldquo;{ev.supporting_text}&rdquo;</p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-muted-foreground py-4">
                      No evidence collected yet. Evidence will appear here after company research is completed.
                    </div>
                  )}
                </CollapsiblePanel>
              </>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Brain size={48} className="mx-auto mb-4 opacity-50" />
                <p>Failed to load company intelligence</p>
                <Button variant="outline" className="mt-4" onClick={fetchCompanyIntelligence}>
                  <RefreshCw size={14} className="mr-2" />
                  Retry
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Overview Tab Content (original content) */}
        {activeTab === "overview" && (
        <div className="lead-detail-container">
          <div className="lead-detail-stack">
            {/* Website Preview Panel */}
            {lead.preview_url && !isSkipped && (
              <CollapsiblePanel
                id="lead-preview"
                title="Website Preview"
                icon={Globe}
                defaultOpen={true}
                defaultOpenMobile={true}
                data-testid="preview-panel"
                badge={
                  <a 
                    href={`${process.env.REACT_APP_BACKEND_URL}${lead.preview_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="ml-auto"
                  >
                    <Button variant="outline" size="sm" data-testid="open-preview-btn">
                      <ExternalLink size={14} className="mr-2" />
                      Open Full Preview
                    </Button>
                  </a>
                }
              >
                <div className="border-t border-border -mx-4 -mt-1">
                  <iframe
                    src={`${process.env.REACT_APP_BACKEND_URL}${lead.preview_url}`}
                    title="Website Preview"
                    className="w-full preview-iframe border-none"
                  />
                </div>
              </CollapsiblePanel>
            )}

            {/* AI Generated Content Panel */}
            {lead.ai_prompt && (
              <CollapsiblePanel
                id="lead-ai-content"
                title="AI Generated Content"
                icon={FileText}
                defaultOpen={false}
                defaultOpenMobile={false}
                data-testid="ai-content-panel"
              >
                <pre className="bg-muted p-4 rounded-lg text-xs overflow-auto max-h-[400px] whitespace-pre-wrap font-mono">
                  {lead.ai_prompt}
                </pre>
              </CollapsiblePanel>
            )}

            {/* Current Status Panel */}
            <CollapsiblePanel
              id="lead-status"
              title="Current Status"
              icon={CheckCircle}
              defaultOpen={true}
              defaultOpenMobile={true}
              data-testid="status-panel"
              badge={<Badge className={`ml-2 ${status.className}`}>{status.label}</Badge>}
            >
              <div className="space-y-4">
                {lead.suppression_reason && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Suppression</span>
                    <Badge className="status-warning">{lead.suppression_reason}</Badge>
                  </div>
                )}
                
                {!isSkipped && (
                  <>
                    <Separator />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground mb-2">Update Status</p>
                        <Select 
                          value={lead.status} 
                          onValueChange={updateStatus}
                          disabled={updating}
                        >
                          <SelectTrigger data-testid="status-select" className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(statusConfig).map(([key, { label }]) => (
                              <SelectItem key={key} value={key}>{label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Campaign leads: Mark Won/Lost buttons */}
                      {!isOneOff && lead.status !== "won" && lead.status !== "lost" && (
                        <>
                          <Button 
                            variant="outline"
                            onClick={() => setMarkWonModalOpen(true)}
                            disabled={updating}
                            data-testid="mark-won-btn"
                            className="h-10 mt-auto"
                          >
                            <Trophy size={14} className="mr-2 text-yellow-500" />
                            Mark Won
                          </Button>
                          <Button 
                            variant="outline"
                            onClick={() => updateStatus("lost")}
                            disabled={updating}
                            data-testid="mark-lost-btn"
                            className="h-10 mt-auto"
                          >
                            <XCircle size={14} className="mr-2" />
                            Mark Lost
                          </Button>
                        </>
                      )}

                      {/* One-off builds: Mark Approved/Cancel buttons */}
                      {isOneOff && lead.status !== "approved" && lead.status !== "lost" && lead.status === "preview_ready" && (
                        <>
                          <Button 
                            variant="outline"
                            onClick={() => updateStatus("approved")}
                            disabled={updating}
                            data-testid="mark-approved-btn"
                            className="h-10 mt-auto"
                          >
                            <CheckCircle size={14} className="mr-2 text-green-500" />
                            Approve Build
                          </Button>
                          <Button 
                            variant="outline"
                            onClick={() => updateStatus("lost")}
                            disabled={updating}
                            data-testid="cancel-build-btn"
                            className="h-10 mt-auto"
                          >
                            <XCircle size={14} className="mr-2" />
                            Cancel Build
                          </Button>
                        </>
                      )}

                      {/* Block button - only for campaign leads */}
                      {!isOneOff && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              variant="outline" 
                              className="h-10 mt-auto text-red-600 hover:text-red-700 hover:bg-red-50"
                              disabled={updating || lead.suppression_reason === "manual_block"}
                              data-testid="block-lead-btn"
                            >
                              <Ban size={14} className="mr-2" />
                              {lead.suppression_reason === "manual_block" ? "Blocked" : "Block Lead"}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Block this lead?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will prevent this business from being contacted in future campaigns. 
                                This action cannot be easily undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={blockLead} className="bg-red-600 hover:bg-red-700">
                                Block Lead
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </>
                )}
              </div>
            </CollapsiblePanel>

            {/* Contact Information Panel */}
            <CollapsiblePanel
              id="lead-contact"
              title="Contact Information"
              icon={Phone}
              defaultOpen={true}
              defaultOpenMobile={false}
              data-testid="contact-panel"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {lead.email && (
                  <div className="flex items-start gap-3">
                    <Mail size={16} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-muted-foreground">Email</p>
                      <a href={`mailto:${lead.email}`} className="text-sm font-medium hover:text-accent break-all">
                        {lead.email}
                      </a>
                    </div>
                  </div>
                )}
                {lead.phone && (
                  <div className="flex items-start gap-3">
                    <Phone size={16} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-muted-foreground">Phone</p>
                      <a href={`tel:${lead.phone}`} className="text-sm font-medium hover:text-accent">
                        {lead.phone}
                      </a>
                    </div>
                  </div>
                )}
                {lead.address && (
                  <div className="flex items-start gap-3">
                    <MapPin size={16} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-muted-foreground">Address</p>
                      <p className="text-sm font-medium">{lead.address}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <Briefcase size={16} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-muted-foreground">Industry</p>
                    <p className="text-sm font-medium">
                      {lead.primary_industry || lead.category}
                      {lead.industry_group && (
                        <span className="text-muted-foreground"> ({lead.industry_group})</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </CollapsiblePanel>

            {/* Timeline Panel */}
            <CollapsiblePanel
              id="lead-timeline"
              title="Timeline"
              icon={Clock}
              defaultOpen={true}
              defaultOpenMobile={false}
              data-testid="timeline-panel"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="flex items-start gap-3">
                  <Clock size={16} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-muted-foreground">Created</p>
                    <p className="text-sm font-medium">
                      {new Date(lead.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                
                {lead.email_sent_at && (
                  <div className="flex items-start gap-3">
                    <Mail size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-muted-foreground">Email Sent</p>
                      <p className="text-sm font-medium">
                        {new Date(lead.email_sent_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                )}

                {lead.fast_action_expires_at && (
                  <div className="flex items-start gap-3">
                    <Clock size={16} className={`${fastActionExpired ? "text-red-500" : "text-yellow-500"} mt-0.5 flex-shrink-0`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-muted-foreground">Fast-Action Expires</p>
                      <p className={`text-sm font-medium ${fastActionExpired ? 'text-red-500' : ''}`}>
                        {new Date(lead.fast_action_expires_at).toLocaleString()}
                        {fastActionExpired && " (Expired)"}
                      </p>
                    </div>
                  </div>
                )}

                {lead.suppressed_at && (
                  <div className="flex items-start gap-3">
                    <Ban size={16} className="text-yellow-500 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-muted-foreground">Skipped At</p>
                      <p className="text-sm font-medium">
                        {new Date(lead.suppressed_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                )}

                {lead.service_categories?.length > 0 && (
                  <div className="flex items-start gap-3 sm:col-span-2 lg:col-span-4">
                    <Tag size={16} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-muted-foreground">Services</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {lead.service_categories.map((svc, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {svc}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CollapsiblePanel>

            {/* Appointment Info Panel */}
            {appointment && (
              <CollapsiblePanel
                id="lead-appointment"
                title="Scheduled Appointment"
                icon={Calendar}
                defaultOpen={true}
                defaultOpenMobile={true}
                data-testid="appointment-panel"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Date & Time</p>
                    <p className="font-medium">
                      {new Date(appointment.appointment_datetime).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Contact Name</p>
                    <p className="font-medium">{appointment.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Contact Email</p>
                    <p className="font-medium">{appointment.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Status</p>
                    <Badge className="status-success">{appointment.status}</Badge>
                  </div>
                  {appointment.notes && (
                    <div className="sm:col-span-2 lg:col-span-4">
                      <p className="text-sm text-muted-foreground">Notes</p>
                      <p className="font-medium">{appointment.notes}</p>
                    </div>
                  )}
                </div>
              </CollapsiblePanel>
            )}

            {/* Full Site Build Panel (for Won campaign leads or Approved one-off builds) */}
            {canBuildFullSite && (
              <CollapsiblePanel
                id="lead-full-site"
                title="Full Site Build"
                icon={Globe}
                defaultOpen={true}
                defaultOpenMobile={true}
                data-testid="fullsite-panel"
                badge={
                  <Badge className={`ml-2 ${fullSiteStatusConfig[lead.full_site_status || 'none']?.className}`}>
                    {fullSiteStatusConfig[lead.full_site_status || 'none']?.label}
                  </Badge>
                }
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                  {lead.full_site_built_at && (
                    <div>
                      <p className="text-sm text-muted-foreground">Built At</p>
                      <p className="text-sm font-medium">
                        {new Date(lead.full_site_built_at).toLocaleDateString()}
                      </p>
                    </div>
                  )}

                  {lead.full_site_status === "ready" ? (
                    <>
                      <Button 
                        onClick={downloadFullSite}
                        data-testid="download-site-btn"
                      >
                        <Download size={14} className="mr-2" />
                        Download Site ZIP
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => buildFullSite(true)}
                        disabled={buildingSite}
                        data-testid="rebuild-site-btn"
                      >
                        {buildingSite ? (
                          <>
                            <Loader2 size={14} className="mr-2 animate-spin" />
                            Rebuilding...
                          </>
                        ) : (
                          <>
                            <RefreshCw size={14} className="mr-2" />
                            Rebuild Site
                          </>
                        )}
                      </Button>
                    </>
                  ) : (
                    <Button 
                      onClick={() => buildFullSite(false)}
                      disabled={buildingSite || lead.full_site_status === "building"}
                      data-testid="build-site-btn"
                    >
                      {buildingSite || lead.full_site_status === "building" ? (
                        <>
                          <Loader2 size={14} className="mr-2 animate-spin" />
                          Building Site...
                        </>
                      ) : (
                        <>
                          <Globe size={14} className="mr-2" />
                          Build Full Site
                        </>
                      )}
                    </Button>
                  )}
                  
                  <p className="text-xs text-muted-foreground sm:col-span-2 lg:col-span-4">
                    Generates a complete multi-page website with all services, service areas, about, and contact pages.
                  </p>
                </div>
              </CollapsiblePanel>
            )}

            {/* Brand Assets Panel */}
            <CollapsiblePanel
              id="lead-brand-assets"
              title="Brand Assets"
              icon={Image}
              defaultOpen={true}
              defaultOpenMobile={false}
              data-testid="brand-assets-panel"
            >
              <BrandAssets 
                leadId={leadId} 
                leadStatus={lead.status}
                sourceType={lead.source_type}
                onUpdate={fetchLeadDetails}
                embedded={true}
              />
            </CollapsiblePanel>

            {/* Client Intake Panel (for Won campaign leads or Approved one-off builds) */}
            {canBuildFullSite && (
              <CollapsiblePanel
                id="lead-client-intake"
                title={isOneOff ? "Build Details (Admin Only)" : "Client Intake (Admin Only)"}
                icon={UserCheck}
                defaultOpen={true}
                defaultOpenMobile={false}
                data-testid="intake-panel"
              >
                <ClientIntakeForm 
                  leadId={leadId}
                  onUpdate={fetchLeadDetails}
                  embedded={true}
                />
              </CollapsiblePanel>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Mark Won Modal - only for campaign leads */}
      {!isOneOff && (
        <MarkWonModal
          leadId={leadId}
          open={markWonModalOpen}
          onOpenChange={setMarkWonModalOpen}
          onSuccess={() => {
            fetchLeadDetails();
            toast.success("Lead marked as Won! You can now upload brand assets.");
          }}
        />
      )}
    </div>
  );
}
